'use client';

import React, { useState, useEffect } from 'react';
import ClientCategoryCard from './ClientCategoryCard';
import StatsSection from './StatsSection';
import ClientShowcase from './ClientShowcase';

interface ClientType {
  name: string;
  description: string;
  image: string;
  alt: string;
}

interface ProjectExample {
  title: string;
  description: string;
  outcome: string;
}

interface ClientCategory {
  title: string;
  description: string;
  icon: string;
  clientTypes: ClientType[];
  projectExamples: ProjectExample[];
  testimonial: {
    quote: string;
    author: string;
    position: string;
  };
  bgColor: string;
  iconColor: string;
}

interface Stat {
  number: string;
  label: string;
  icon: string;
  description: string;
}

interface ShowcaseClient {
  category: string;
  image: string;
  alt: string;
  services: string[];
  highlight: string;
}

const TargetClientsInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const clientCategories: ClientCategory[] = [
  {
    title: "Government Institutions",
    description: "Public sector organizations and agencies",
    icon: "BuildingLibraryIcon",
    bgColor: "bg-gradient-to-r from-blue-600 to-blue-700",
    iconColor: "bg-blue-800",
    clientTypes: [
    {
      name: "Ministries & Departments",
      description: "Central government offices requiring bulk supplies and services",
      image: "https://images.unsplash.com/photo-1671830093124-819f0ce89bad",
      alt: "Modern government building with glass facade and Kenyan flag"
    },
    {
      name: "County Governments",
      description: "Local administration offices and public service centers",
      image: "https://images.unsplash.com/photo-1720698420295-8ccd5b093e2a",
      alt: "County government office building with people walking in courtyard"
    },
    {
      name: "Parastatals",
      description: "State corporations and public enterprises",
      image: "https://images.unsplash.com/photo-1656646425073-a1ec4a84e0dc",
      alt: "Corporate office building with modern architecture and landscaped entrance"
    },
    {
      name: "Public Hospitals",
      description: "Healthcare facilities requiring medical and general supplies",
      image: "https://images.unsplash.com/photo-1720605739861-9f5110c7e529",
      alt: "Modern hospital building exterior with ambulance bay and medical signage"
    }],

    projectExamples: [
    {
      title: "Ministry Office Renovation",
      description: "Complete office furniture and equipment supply for 200+ workstations",
      outcome: "Delivered 2 weeks ahead of schedule"
    },
    {
      title: "County Cleaning Contract",
      description: "6-month comprehensive cleaning services for multiple county offices",
      outcome: "98% client satisfaction rating"
    },
    {
      title: "Hospital Equipment Supply",
      description: "Medical equipment and consumables for regional hospital expansion",
      outcome: "Zero delivery delays, full compliance"
    }],

    testimonial: {
      quote: "LARSCAR consistently delivers quality supplies on time and within budget. Their understanding of government procurement processes is exceptional.",
      author: "Sarah Mwangi",
      position: "Procurement Officer, County Government"
    }
  },
  {
    title: "Corporate Organizations",
    description: "Private companies and multinational corporations",
    icon: "BuildingOffice2Icon",
    bgColor: "bg-gradient-to-r from-emerald-600 to-emerald-700",
    iconColor: "bg-emerald-800",
    clientTypes: [
    {
      name: "Banks & Financial Services",
      description: "Financial institutions requiring security and office solutions",
      image: "https://images.unsplash.com/photo-1652445953283-2e587d42e8d9",
      alt: "Modern bank building with glass exterior and professional entrance"
    },
    {
      name: "Manufacturing Companies",
      description: "Industrial facilities needing equipment and maintenance services",
      image: "https://images.unsplash.com/photo-1650356974277-db0ce9172eb3",
      alt: "Industrial manufacturing facility with workers in safety gear"
    },
    {
      name: "Retail Chains",
      description: "Multi-location retailers requiring consistent supply solutions",
      image: "https://images.unsplash.com/photo-1549194388-2469d59ec75c",
      alt: "Modern retail store interior with organized shelving and bright lighting"
    },
    {
      name: "Tech Companies",
      description: "IT firms needing office setup and technical equipment",
      image: "https://images.unsplash.com/photo-1710905281898-990953229065",
      alt: "Modern tech office with open workspace and employees at computers"
    }],

    projectExamples: [
    {
      title: "Bank Branch Setup",
      description: "Complete furniture and security equipment for 5 new branches",
      outcome: "All branches operational on schedule"
    },
    {
      title: "Factory Maintenance Contract",
      description: "Ongoing cleaning and maintenance services for manufacturing facility",
      outcome: "15% reduction in downtime"
    },
    {
      title: "Office Relocation Project",
      description: "Full office moving and setup services for 300-employee company",
      outcome: "Zero business disruption"
    }],

    testimonial: {
      quote: "Their corporate solutions are comprehensive and cost-effective. LARSCAR has become our go-to partner for all facility needs.",
      author: "James Kiprotich",
      position: "Facilities Manager, Regional Bank"
    }
  },
  {
    title: "NGOs & International Organizations",
    description: "Non-profit and development organizations",
    icon: "GlobeAltIcon",
    bgColor: "bg-gradient-to-r from-purple-600 to-purple-700",
    iconColor: "bg-purple-800",
    clientTypes: [
    {
      name: "International NGOs",
      description: "Global organizations with local operations and projects",
      image: "https://images.unsplash.com/photo-1662748562754-b19fd06169de",
      alt: "NGO office with volunteers working at desks with world map on wall"
    },
    {
      name: "Local Charities",
      description: "Community-based organizations serving local populations",
      image: "https://images.unsplash.com/photo-1593113646773-028c64a8f1b8",
      alt: "Community center with volunteers distributing supplies to families"
    },
    {
      name: "UN Agencies",
      description: "United Nations offices and humanitarian programs",
      image: "https://images.unsplash.com/photo-1652384917540-18afcc36195b",
      alt: "International organization meeting room with flags and conference setup"
    },
    {
      name: "Development Partners",
      description: "Bilateral and multilateral development organizations",
      image: "https://images.unsplash.com/photo-1675923038634-c5641e9013fa",
      alt: "Development organization office with staff reviewing project documents"
    }],

    projectExamples: [
    {
      title: "Humanitarian Response Supply",
      description: "Emergency supplies and logistics for disaster relief operations",
      outcome: "24-hour emergency response time"
    },
    {
      title: "Conference & Event Management",
      description: "Complete event setup for 500-person international conference",
      outcome: "Flawless execution, positive feedback"
    },
    {
      title: "Field Office Setup",
      description: "Rapid deployment of office equipment for remote field operations",
      outcome: "Operational within 48 hours"
    }],

    testimonial: {
      quote: "LARSCAR understands the unique challenges of NGO operations. Their flexibility and reliability are crucial for our mission success.",
      author: "Dr. Patricia Wanjiku",
      position: "Country Director, International Development NGO"
    }
  },
  {
    title: "Educational Institutions",
    description: "Schools, colleges, and training centers",
    icon: "AcademicCapIcon",
    bgColor: "bg-gradient-to-r from-amber-600 to-amber-700",
    iconColor: "bg-amber-800",
    clientTypes: [
    {
      name: "Universities",
      description: "Higher education institutions requiring comprehensive solutions",
      image: "https://images.unsplash.com/photo-1623725699550-308935cfaa27",
      alt: "University campus with students walking between modern academic buildings"
    },
    {
      name: "Secondary Schools",
      description: "High schools needing furniture, equipment, and maintenance services",
      image: "https://images.unsplash.com/photo-1657004508178-5374531174db",
      alt: "Secondary school classroom with students at desks and teacher at blackboard"
    },
    {
      name: "Primary Schools",
      description: "Elementary schools requiring child-friendly furniture and supplies",
      image: "https://images.unsplash.com/photo-1600173868782-c9f90ff5175c",
      alt: "Primary school classroom with colorful furniture and educational materials"
    },
    {
      name: "Training Centers",
      description: "Vocational and professional development institutions",
      image: "https://images.unsplash.com/photo-1581092160607-ee22621dd758",
      alt: "Training center with modern equipment and students in technical workshop"
    }],

    projectExamples: [
    {
      title: "University Library Renovation",
      description: "Complete furniture and IT equipment for 1000-seat library",
      outcome: "Enhanced student satisfaction by 40%"
    },
    {
      title: "School Cleaning Program",
      description: "Daily cleaning services for 15 primary schools in rural areas",
      outcome: "Improved hygiene standards significantly"
    },
    {
      title: "Laboratory Equipment Supply",
      description: "Science lab setup for new secondary school campus",
      outcome: "Students achieved 95% pass rate"
    }],

    testimonial: {
      quote: "LARSCAR's educational solutions are tailored to our students' needs. Their quality furniture has transformed our learning environment.",
      author: "Prof. Michael Ochieng",
      position: "Vice Chancellor, Regional University"
    }
  }];


  const stats: Stat[] = [
  {
    number: "500+",
    label: "Active Clients",
    icon: "UsersIcon",
    description: "Trusted partners across all sectors"
  },
  {
    number: "50+",
    label: "Government Contracts",
    icon: "BuildingLibraryIcon",
    description: "Successful public sector projects"
  },
  {
    number: "200+",
    label: "Corporate Partners",
    icon: "BuildingOffice2Icon",
    description: "Private sector collaborations"
  },
  {
    number: "98%",
    label: "Client Retention",
    icon: "HeartIcon",
    description: "Long-term partnership success"
  }];


  const showcaseClients: ShowcaseClient[] = [
  {
    category: "Government Ministry",
    image: "https://images.unsplash.com/photo-1717786970514-f111dc520844",
    alt: "Government ministry building with modern architecture and official entrance",
    services: ["Office Furniture", "IT Equipment", "Cleaning Services"],
    highlight: "Completed 3-month office modernization project with zero delays and full compliance with procurement regulations."
  },
  {
    category: "International Bank",
    image: "https://images.unsplash.com/photo-1652445953283-2e587d42e8d9",
    alt: "Modern bank headquarters with glass facade and professional landscaping",
    services: ["Security Systems", "Branch Setup", "Maintenance"],
    highlight: "Established 8 new branches across Kenya with comprehensive security and furniture solutions."
  },
  {
    category: "Regional University",
    image: "https://images.unsplash.com/photo-1604306748004-5ebc73dd331f",
    alt: "University campus with students walking between lecture halls and library buildings",
    services: ["Laboratory Equipment", "Library Furniture", "Campus Cleaning"],
    highlight: "Transformed learning spaces for 15,000 students with modern furniture and state-of-the-art laboratory equipment."
  },
  {
    category: "Healthcare NGO",
    image: "https://images.unsplash.com/photo-1680759291238-2f4f9fac8d43",
    alt: "NGO healthcare facility with medical staff and community health workers",
    services: ["Medical Supplies", "Event Management", "Logistics"],
    highlight: "Supported rural healthcare initiatives reaching over 50,000 beneficiaries with reliable supply chain management."
  },
  {
    category: "Manufacturing Company",
    image: "https://images.unsplash.com/photo-1656665421925-52c929526aea",
    alt: "Industrial manufacturing facility with workers in safety equipment and production lines",
    services: ["Industrial Cleaning", "Equipment Supply", "Maintenance"],
    highlight: "Reduced operational downtime by 25% through comprehensive facility management and preventive maintenance."
  },
  {
    category: "Tech Startup Hub",
    image: "https://images.unsplash.com/photo-1556761175-4b46a572b786",
    alt: "Modern tech office space with open workstations and collaborative areas",
    services: ["Office Setup", "IT Infrastructure", "Co-working Solutions"],
    highlight: "Enabled rapid scaling for 20+ startups with flexible office solutions and modern workspace design."
  }];


  if (!isHydrated) {
    return (
      <div data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:355:6" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="355" data-component-file="TargetClientsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:356:8" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="356" data-component-file="TargetClientsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container-max%20py-16%22%7D" className="container-max py-16">
          <div data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:357:10" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="357" data-component-file="TargetClientsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
            <div data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:358:12" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="358" data-component-file="TargetClientsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-muted%20rounded%20w-1%2F3%20mx-auto%22%7D" className="h-8 bg-muted rounded w-1/3 mx-auto"></div>
            <div data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:359:12" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="359" data-component-file="TargetClientsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-4%20bg-muted%20rounded%20w-2%2F3%20mx-auto%22%7D" className="h-4 bg-muted rounded w-2/3 mx-auto"></div>
            <div data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:360:12" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="360" data-component-file="TargetClientsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-2%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {[1, 2, 3, 4].map((i) =>
              <div data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:362:14" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="362" data-component-file="TargetClientsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-xl%22%7D" key={i} className="h-96 bg-muted rounded-xl"></div>
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:372:4" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="372" data-component-file="TargetClientsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      {/* Hero Section */}
      <div data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:374:6" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="374" data-component-file="TargetClientsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-gradient-to-br%20from-primary%20via-secondary%20to-primary%20py-20%22%7D" className="bg-gradient-to-br from-primary via-secondary to-primary py-20">
        <div data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:375:8" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="375" data-component-file="TargetClientsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container-max%20text-center%22%7D" className="container-max text-center">
          <h1 data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:376:10" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="376" data-component-file="TargetClientsInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-4xl%20lg%3Atext-6xl%20font-bold%20text-white%20mb-6%22%2C%22textContent%22%3A%22Our%22%7D" className="text-4xl lg:text-6xl font-bold text-white mb-6">
            Our <span data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:377:16" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="377" data-component-file="TargetClientsInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-accent%22%2C%22textContent%22%3A%22Target%20Clients%22%7D" className="text-accent">Target Clients</span>
          </h1>
          <p data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:379:10" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="379" data-component-file="TargetClientsInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xl%20text-white%2F90%20max-w-3xl%20mx-auto%20mb-8%22%2C%22textContent%22%3A%22Serving%20diverse%20sectors%20across%20Kenya%20with%20tailored%20solutions%20that%20meet%20unique%20industry%20needs%20and%20regulatory%20requirements.%22%7D" className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
            Serving diverse sectors across Kenya with tailored solutions that meet unique industry needs and regulatory requirements.
          </p>
          <div data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:382:10" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="382" data-component-file="TargetClientsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20justify-center%20gap-4%20text-white%2F80%22%7D" className="flex flex-wrap justify-center gap-4 text-white/80">
            <span data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:383:12" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="383" data-component-file="TargetClientsInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22bg-white%2F20%20px-4%20py-2%20rounded-full%20text-sm%20font-medium%22%2C%22textContent%22%3A%22Government%20Institutions%22%7D" className="bg-white/20 px-4 py-2 rounded-full text-sm font-medium">Government Institutions</span>
            <span data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:384:12" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="384" data-component-file="TargetClientsInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22bg-white%2F20%20px-4%20py-2%20rounded-full%20text-sm%20font-medium%22%2C%22textContent%22%3A%22Corporate%20Organizations%22%7D" className="bg-white/20 px-4 py-2 rounded-full text-sm font-medium">Corporate Organizations</span>
            <span data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:385:12" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="385" data-component-file="TargetClientsInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22bg-white%2F20%20px-4%20py-2%20rounded-full%20text-sm%20font-medium%22%2C%22textContent%22%3A%22NGOs%20%26%20International%22%7D" className="bg-white/20 px-4 py-2 rounded-full text-sm font-medium">NGOs & International</span>
            <span data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:386:12" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="386" data-component-file="TargetClientsInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22bg-white%2F20%20px-4%20py-2%20rounded-full%20text-sm%20font-medium%22%2C%22textContent%22%3A%22Educational%20Institutions%22%7D" className="bg-white/20 px-4 py-2 rounded-full text-sm font-medium">Educational Institutions</span>
          </div>
        </div>
      </div>

      {/* Client Categories */}
      <div data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:392:6" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="392" data-component-file="TargetClientsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22section-padding%22%7D" className="section-padding">
        <div data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:393:8" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="393" data-component-file="TargetClientsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container-max%22%7D" className="container-max">
          <div data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:394:10" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="394" data-component-file="TargetClientsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-16%22%7D" className="text-center mb-16">
            <h2 data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:395:12" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="395" data-component-file="TargetClientsInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20lg%3Atext-4xl%20font-bold%20text-text-primary%20mb-4%22%2C%22textContent%22%3A%22Client%20Categories%20We%20Serve%22%7D" className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Client Categories We Serve
            </h2>
            <p data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:398:12" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="398" data-component-file="TargetClientsInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20text-lg%20max-w-3xl%20mx-auto%22%2C%22textContent%22%3A%22Our%20expertise%20spans%20multiple%20sectors%2C%20each%20with%20unique%20requirements%20and%20challenges.%20We've%20developed%20specialized%20approaches%20to%20serve%20each%20client%20category%20effectively.%22%7D" className="text-text-secondary text-lg max-w-3xl mx-auto">
              Our expertise spans multiple sectors, each with unique requirements and challenges. We've developed specialized approaches to serve each client category effectively.
            </p>
          </div>

          <div data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:403:10" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="403" data-component-file="TargetClientsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-2%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {clientCategories.map((category, index) =>
            <ClientCategoryCard data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:405:12" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="405" data-component-file="TargetClientsInteractive.tsx" data-component-name="ClientCategoryCard" data-component-content="%7B%22elementName%22%3A%22ClientCategoryCard%22%7D"
              key={index}
              title={category.title}
              description={category.description}
              icon={category.icon}
              clientTypes={category.clientTypes}
              projectExamples={category.projectExamples}
              testimonial={category.testimonial}
              bgColor={category.bgColor}
              iconColor={category.iconColor} />

            )}
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <StatsSection data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:422:6" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="422" data-component-file="TargetClientsInteractive.tsx" data-component-name="StatsSection" data-component-content="%7B%22elementName%22%3A%22StatsSection%22%7D" stats={stats} />

      {/* Client Showcase */}
      <ClientShowcase data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:425:6" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="425" data-component-file="TargetClientsInteractive.tsx" data-component-name="ClientShowcase" data-component-content="%7B%22elementName%22%3A%22ClientShowcase%22%7D" clients={showcaseClients} />

      {/* Call to Action */}
      <div data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:428:6" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="428" data-component-file="TargetClientsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22section-padding%20bg-surface%22%7D" className="section-padding bg-surface">
        <div data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:429:8" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="429" data-component-file="TargetClientsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container-max%20text-center%22%7D" className="container-max text-center">
          <h2 data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:430:10" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="430" data-component-file="TargetClientsInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20lg%3Atext-4xl%20font-bold%20text-text-primary%20mb-4%22%2C%22textContent%22%3A%22Ready%20to%20Join%20Our%20Client%20Family%3F%22%7D" className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Ready to Join Our Client Family?
          </h2>
          <p data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:433:10" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="433" data-component-file="TargetClientsInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20text-lg%20max-w-2xl%20mx-auto%20mb-8%22%2C%22textContent%22%3A%22Whether%20you're%20a%20government%20institution%2C%20corporate%20organization%2C%20NGO%2C%20or%20educational%20facility%2C%20we%20have%20the%20expertise%20and%20solutions%20to%20meet%20your%20unique%20needs.%22%7D" className="text-text-secondary text-lg max-w-2xl mx-auto mb-8">
            Whether you're a government institution, corporate organization, NGO, or educational facility, we have the expertise and solutions to meet your unique needs.
          </p>
          <div data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:436:10" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="436" data-component-file="TargetClientsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20sm%3Aflex-row%20gap-4%20justify-center%22%7D" className="flex flex-col sm:flex-row gap-4 justify-center">
            <a data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:437:12" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="437" data-component-file="TargetClientsInteractive.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22href%22%3A%22%2Fcontact-hub%22%2C%22className%22%3A%22btn-conversion%22%2C%22textContent%22%3A%22Get%20Your%20Custom%20Quote%22%7D" href="/contact-hub" className="btn-conversion">
              Get Your Custom Quote
            </a>
            <a data-component-id="src/app/target-clients/components/TargetClientsInteractive.tsx:440:12" data-component-path="src/app/target-clients/components/TargetClientsInteractive.tsx" data-component-line="440" data-component-file="TargetClientsInteractive.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22href%22%3A%22%2Fservices%22%2C%22className%22%3A%22btn-secondary%22%2C%22textContent%22%3A%22Explore%20Our%20Services%22%7D" href="/services" className="btn-secondary">
              Explore Our Services
            </a>
          </div>
        </div>
      </div>
    </div>);

};

export default TargetClientsInteractive;